import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {ConfigProvider, theme} from 'antd';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { getRoutes } from './routes';
import './App.css';

// Utility function to update favicon dynamically
const updateFavicon = (faviconUrl: string): void => {
  // Find existing favicon link element
  let faviconLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement;

  if (faviconLink) {
    // Update existing favicon
    faviconLink.href = faviconUrl;
  } else {
    // Create new favicon link element
    faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.href = faviconUrl;
    document.head.appendChild(faviconLink);
  }
};

// Component to handle dynamic title and favicon updates
const AppContent: React.FC<{
  isAuthenticated: boolean;
  handleLogin: (token: string) => void;
  handleLogout: () => void;
}> = ({ isAuthenticated, handleLogin, handleLogout }) => {
  const { settings, loading: settingsLoading } = useSettings();

  // Update title and favicon when settings are loaded
  useEffect(() => {
    if (!settingsLoading && settings) {
      // Update page title with company name
      if (settings.com_name) {
        document.title = settings.com_name;
      }

      // Update favicon if available
      if (settings.favicon) {
        const faviconUrl = `${process.env.REACT_APP_API_URL}/file/public/view?file=${settings.favicon}`;
        updateFavicon(faviconUrl);
      }
    }
  }, [settings, settingsLoading]);

  return (
    <Router>
      <div className="App">
        <Routes>
          {getRoutes(isAuthenticated, handleLogin, handleLogout).map((route, index) => (
            <Route key={index} path={route.path} element={route.element} />
          ))}
        </Routes>
      </div>
    </Router>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check if user is already logged in (check localStorage or token)
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = (token: string): void => {
    localStorage.setItem('authToken', token);
    setIsAuthenticated(true);
  };

  const handleLogout = (): void => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <SettingsProvider>
      <ConfigProvider
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: {
            colorPrimary: '#1890ff',
          },
        }}
      >
        <AppContent
          isAuthenticated={isAuthenticated}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
        />
      </ConfigProvider>
    </SettingsProvider>
  );
};

export default App;
